import React, { useState, useRef, forwardRef, useCallback, useMemo, useEffect, useImperativeHandle } from 'react';
import Select, { components } from 'react-select';
import {
   Box,
   FormControl,
   FormLabel,
   FormErrorMessage,
   Text,
   Button,
   Icon,
   useColorModeValue,
   useDisclosure,
   VStack,
} from '@chakra-ui/react';
import { FiEdit } from 'react-icons/fi';
import { VariableSizeList as List } from 'react-window';
import { useGlobal, useUser } from '../../contexts';
import BankAccountsForm from '../../views/admin/masters/Pages/BankAccounts/BankAccountsForm';
import BrandMasterForm from '../../views/admin/masters/Pages/BrandMaster/BrandMasterForm';
import CityMasterForm from '../../views/admin/masters/Pages/CityMaster/CityMasterForm';
import StateMasterForm from '../../views/admin/masters/Pages/StateMaster/StateMasterForm';
import CountryMasterForm from '../../views/admin/masters/Pages/CountryMaster/CountryMasterForm';
import TransportMasterForm from '../../views/admin/masters/Pages/TransportMaster/TransportMasterForm';
import ItemMasterForm from '../../views/admin/masters/Pages/ItemMaster/ItemMasterForm';
import LedgerMasterForm from '../../views/admin/masters/Pages/LedgerMaster/LedgerMasterForm';
import SalesRepresentativeMasterForm from '../../views/admin/masters/Pages/SalesRepresentativeMaster/SalesRepresentativeMasterForm';
import HsnMasterForm from '../../views/admin/masters/Pages/HsnMaster/HsnMasterForm';
import AddEditForm from 'views/admin/masters/Pages/AllAccountMaster/AddEditFormForAccount';
import ItemGroupMasterForm from 'views/admin/masters/Pages/ItemGroupMaster/ItemGroupMasterForm';
import WarehouseMasterForm from 'views/admin/masters/Pages/WarehouseMaster/WarehouseMasterForm';
import DaybookForm from 'views/admin/masters/Pages/DaybookMaster/DaybookForm';
import SeriesMasterForm from 'views/admin/masters/Pages/SeriesMaster/SeriesMasterForm';
import ColorMasterForm from 'views/admin/masters/Pages/ColorMaster/ColorMasterForm';
import VehicleMasterForm from 'views/admin/masters/Pages/VehicleMaster/VehicleMasterForm';
import AddUserModal from '../../components/navbar/ManageUsers/ManageUserForm';
import ScreenMasterForm from '../../views/admin/masters/Pages/ScreenMaster/ScreenMasterForm';
import { useDispatch } from 'react-redux';
import { getAllDaybooks } from '../../redux/actions/daybook';

const VirtualizedMenuList = ({ options, children, maxHeight, getValue, selectOption, focusedOption, selectProps }) => {
   const height = Math.min(maxHeight || 300, options.length * 35);

   const listRef = useRef(null);
   const itemHeightsRef = useRef({});

   const getItemSize = (index) => {
      return itemHeightsRef.current[index] || 35; // fallback default
   };

   useEffect(() => {
      if (focusedOption && listRef.current) {
         const index = options.findIndex((option) => option.value === focusedOption.value);
         if (index >= 0) {
            listRef.current.scrollToItem(index, 'smart');
         }
      }
   }, [focusedOption, options]);

   const bgcolor = useColorModeValue('#ffffff', '#101f4a');

   const Row = ({ index, style }) => {
      const option = options[index];
      const isSelected = getValue().some((selected) => selected.value === option.value);
      const isFocused = focusedOption?.value === option.value;

      useEffect(() => {
         const timer = setTimeout(() => {
            if (listRef.current) {
               listRef.current.resetAfterIndex(0, true); // ✅ full remeasure
            }
         }, 1); // slight delay to allow initial rendering

         return () => clearTimeout(timer);
      }, [options]);

      const refCallback = (node) => {
         if (node) {
            const height = node.getBoundingClientRect().height;
            if (itemHeightsRef.current[index] !== height) {
               itemHeightsRef.current[index] = height;

               // ✅ Only reset if listRef is ready
               if (listRef.current) {
                  listRef.current.resetAfterIndex(index);
               }
            }
         }
      };

      return (
         <div style={style}>
            <div ref={refCallback}>
               {React.cloneElement(children[index], {
                  key: option.value,
                  isSelected,
                  isFocused,
                  innerProps: {
                     onClick: () => selectOption(option),
                     onMouseEnter: () => { },
                  },
               })}
            </div>
         </div>
      );
   };

   return (
      <List
         ref={listRef}
         height={height}
         itemCount={options.length}
         itemSize={getItemSize}
         overscanCount={5}
         backgroundColor={bgcolor}>
         {Row}
      </List>
   );
};

// Optimized Option component with virtualization support
const OptimizedOption = ({ data, innerRef, innerProps, isFocused, isSelected, selectProps, isMulti }) => {
   const handleIconClick = useCallback(
      (e) => {
         e.stopPropagation();
         selectProps.handleEdit?.(data);
      },
      [data, selectProps],
   );
   const { company } = useGlobal();

   const { user } = useUser();

   const bgcolor = useColorModeValue('#ffffff', '#101f4a');
   const namecolor = useColorModeValue('#000000', '#ffffff');

   const backgroundColor = useMemo(() => {
      if (isSelected && !isMulti) return '#3182ce';
      if (isFocused) return isSelected ? '#3182ce' : '#ebf8ff';
      return bgcolor;
   }, [isSelected, isFocused, isMulti]);

   const textColor = isSelected && !isMulti ? 'white' : 'black';
   const itemStockShow = ['item'].includes(selectProps.openfrom) && data.stock !== 0 && data?.stock && !selectProps.hideStock;
   const itemStock2Show = ['item'].includes(selectProps.openfrom) && data.stock2 !== 0 && data?.stock2 && !selectProps.hideStock;
   const itemStock3Show = ['item'].includes(selectProps.openfrom) && data.stock3 !== 0 && data?.stock3 && !selectProps.hideStock;

   return (
      <>
         <Box
            ref={innerRef}
            {...innerProps}
            bg={backgroundColor}
            color={textColor}
            px={3}
            py={2}
            cursor="pointer"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            textColor={isSelected ? '#ffffff' : isFocused ? '#000000' : namecolor}
         // minHeight="35px"
         >
            <Box flex="2" height="auto">
               <VStack align="start" spacing={1}>
                  <Text fontSize="sm">{data.label}</Text>

                  {selectProps.showBalance && data.bal !== undefined && data.bal !== 0 && (
                     <Text fontSize="xs" color={isSelected ? '#ffffff' : data.bal > 0 ? 'red.500' : 'green.500'}>
                        Balance: ₹{Math.abs(data.bal).toFixed(2)} {data.bal > 0 ? 'CR' : 'DB'}
                     </Text>
                  )}

                  {itemStockShow && company.setting.maxUOM >= 1 && (
                     <Text fontSize="sm" color={isSelected ? '#ffffff' : data.itemdetailtype === 1 ? 'green.500' : 'blue.500'}>
                        {data?.uqc1Data?.name}: {data.stock}
                     </Text>
                  )}

                  {itemStock2Show && company.setting.maxUOM >= 2 && (
                     <Text fontSize="sm" color={isSelected ? '#ffffff' : data.itemdetailtype === 1 ? 'green.500' : 'blue.500'}>
                        {data?.uqc2Data?.name}: {data.stock2}
                     </Text>
                  )}

                  {itemStock3Show && company.setting.maxUOM >= 3 && (
                     <Text fontSize="sm" color={isSelected ? '#ffffff' : data.itemdetailtype === 1 ? 'green.500' : 'blue.500'}>
                        {data?.uqc3Data?.name}: {data.stock3}
                     </Text>
                  )}
               </VStack>
            </Box>
            {selectProps.showEdit &&
               !['city', 'state', 'country', 'ledger'].includes(selectProps.openfrom) &&
               !user.isExpired && (
                  <Icon
                     as={FiEdit}
                     boxSize={4}
                     color={isSelected ? 'white' : 'gray.500'}
                     onClick={handleIconClick}
                     cursor="pointer"
                  />
               )}
         </Box>
      </>
   );
};

// Checkbox Option for multi-select
const CheckboxOption = ({ data, isSelected, innerProps, label, isMulti, isFocused }) => {
   const isSelectAll = data.value === '*';
   const backgroundColor = useMemo(() => {
      if (isSelected && !isMulti) return '#3182ce';
      if (isFocused) return isSelected ? '#3182ce' : '#ebf8ff';
      return 'white';
   }, [isSelected, isFocused, isMulti]);
   const textColor = isSelected && !isMulti ? 'white' : 'black';

   return (
      <Box
         {...innerProps}
         px={3}
         py={2}
         bg={backgroundColor}
         color={textColor}
         cursor="pointer"
         display="flex"
         alignItems="center">
         <input type="checkbox" checked={isSelected} onChange={() => { }} style={{ marginRight: 8 }} />
         {isSelectAll ? <Text fontWeight="bold">{label}</Text> : <Text>{label}</Text>}
      </Box>
   );
};

// Custom Value Container for multi-select display optimization
const CustomValueContainer = ({ children, getValue, selectProps, ...props }) => {
   const selected = getValue();

   const handleClick = useCallback(() => {
      selectProps.setMenuIsOpen?.(true);
   }, [selectProps]);

   const displayText = useMemo(() => {
      if (!selectProps.isMulti || selected.length <= 1) {
         return children;
      }

      const allSelected = selected.length === selectProps.totalOptions;
      if (allSelected) {
         return 'All Selected';
      }

      const firstLabel = selected[0]?.label || '';
      const extraCount = selected.length - 1;
      return `${firstLabel} + ${extraCount} more`;
   }, [selected, selectProps.isMulti, selectProps.totalOptions, children]);

   if (selectProps.isMulti && selected.length > 1) {
      return (
         <components.ValueContainer {...props}>
            <div onClick={handleClick} style={{ cursor: 'pointer', width: '100%' }}>
               {displayText}
            </div>
         </components.ValueContainer>
      );
   }

   return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
};

// Custom No Options Message
const CustomNoOptionsMessage = ({ selectProps, ...props }) => {
   const { user } = useUser();
   const inputValue = selectProps.inputValue || '';
   const bgcolor = useColorModeValue('#ffffff', '#101f4a');

   const handleAddNew = useCallback(
      (e) => {
         e.stopPropagation();
         e.preventDefault();
         selectProps.onAddNew?.(inputValue);
      },
      [inputValue, selectProps],
   );

   return (
      <components.NoOptionsMessage {...props}>
         <Box width="100%" textAlign="center" py={4} backgroundColor={bgcolor}>
            <Text color="gray.500" mb={2}>
               No options found
            </Text>
            {selectProps.showCustomNoOptions && !user.isExpired && (
               <Button size="sm" colorScheme="blue" variant="outline" onMouseDown={handleAddNew}>
                  Add "{inputValue}"
               </Button>
            )}
         </Box>
      </components.NoOptionsMessage>
   );
};

// Optimized search/filter function
const filterOptions = (options, inputValue) => {
   if (!inputValue) return options;


   const searchValue = inputValue.replace(/\s+/g, '').toLowerCase();
   const filtered = options.filter(
      (option) =>
         option.label.replace(/\s+/g, '').toLowerCase().includes(searchValue) ||
         option.value.toString().toLowerCase().includes(searchValue),
   );

   // Sort by relevance (exact matches first, then starts with, then contains)
   return filtered.sort((a, b) => {
      const aLabel = a.label.toLowerCase();
      const bLabel = b.label.toLowerCase();

      const aExact = aLabel === searchValue;
      const bExact = bLabel === searchValue;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      const aStarts = aLabel.startsWith(searchValue);
      const bStarts = bLabel.startsWith(searchValue);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;

      return aLabel.localeCompare(bLabel);
   });
};

const DynamicDropdown = forwardRef(
   (
      {
         name,
         errorName,
         label,
         openfrom = null,
         hideStock,
         daybook,
         type = null,
         showCustomNoOptions = false,
         isMulti = false,
         options = [],
         value,
         onKeyDown,
         errors = {},
         textColor = 'gray.700',
         isRequired = false,
         placeholder = 'Select an option',
         id,
         onChange,
         isInvalid,
         maxMenuHeight = 300,
         virtualized = true, // Enable virtualization for large datasets
         ...props
      },
      ref,
   ) => {
      // State management
      const [isFocused, setIsFocused] = useState(false);
      const [inputValue, setInputValue] = useState('');
      const [menuIsOpen, setMenuIsOpen] = useState(false);
      const [isEditMode, setIsEditMode] = useState(false);
      const [currentRow, setCurrentRow] = useState({});
      const [demoname, setDemoname] = useState('');
      const [isOpen, setIsOpen] = useState(false);
      const { companyList, company } = useGlobal();

      // Refs
      const selectRef = useRef(null);
      const dispatch = useDispatch();
      const { user } = useUser();
      const { onOpen, onClose } = useDisclosure();

      // Theme values
      const bg = useColorModeValue('white', '#2d3748');
      const bg2 = useColorModeValue('#F1F5F9', '#2d3748');
      const fontcolor = useColorModeValue('#9E9E9E', '#ffffff');

      // Memoized computed values
      const mappedOptions = useMemo(() => {
         if (!Array.isArray(options)) return [];

         const baseOptions = options.map((opt) => ({
            ...opt,
            value: opt.id,
            label: opt.label || opt.name || opt.id?.toString() || '',
         }));

         if (isMulti) {
            const allOptionValues = baseOptions.map((opt) => opt.value);
            const isAllSelected = value?.length === allOptionValues.length;
            const selectAllOption = {
               value: '*',
               label: isAllSelected ? 'Deselect All' : 'Select All',
            };
            return [selectAllOption, ...baseOptions];
         }

         return baseOptions;
      }, [options, isMulti, value]);

      // Filtered options based on search
      const filteredOptions = useMemo(() => {
         return filterOptions(mappedOptions, inputValue);
      }, [mappedOptions, inputValue]);

      // Selected option(s)
      const selectedOption = useMemo(() => {
         if (isMulti) {
            if (!value || value.length === 0) return [];

            const allOptionValues = mappedOptions.filter((opt) => opt.value !== '*').map((opt) => opt.value);
            const isAllSelected = value.length === allOptionValues.length;

            if (isAllSelected) {
               return [
                  mappedOptions[0],
                  ...mappedOptions.filter((opt) => value.includes(opt.value) && opt.value !== '*'),
               ];
            }

            return mappedOptions.filter((opt) => value.includes(opt.value));
         }

         return mappedOptions.find((opt) => opt.value === value) || null;
      }, [mappedOptions, value, isMulti]);

      // Floating label logic
      const isFloating = isFocused || (value !== null && value !== undefined && value !== '');

      // Option selection handler
      const handleOptionSelect = useCallback(
         (option) => {
            if (isMulti) {
               const currentValue = value || [];

               if (option.value === '*') {
                  // Handle select/deselect all
                  const allValues = mappedOptions.filter((opt) => opt.value !== '*').map((opt) => opt.value);
                  const newValue = currentValue.length === allValues.length ? [] : allValues;
                  onChange(newValue);
               } else {
                  // Handle individual option toggle
                  const newValue = currentValue.includes(option.value)
                     ? currentValue.filter((v) => v !== option.value)
                     : [...currentValue, option.value];
                  onChange(newValue);
               }
            } else {
               onChange(option.value);
               setMenuIsOpen(false);
            }
         },
         [isMulti, value, onChange, mappedOptions],
      );
      const handleKeyDown = useCallback(
         (e) => {
            onKeyDown?.(e);

            const isCtrlEnter = e.ctrlKey && e.key === 'Enter';
            const isEnter = e.key === 'Enter' && !e.ctrlKey;
            const isTab = e.key === 'Tab' && !e.shiftKey;
            const isArrowDown = e.key === 'ArrowDown';
            const isArrowUp = e.key === 'ArrowUp';
            const isShiftTab = e.shiftKey && e.key === 'Tab';

            // Handle Ctrl+Enter for adding new options
            if (isCtrlEnter && !user.isExpired) {
               e.preventDefault();
               setIsOpen(true);
               setDemoname(inputValue);
               setCurrentRow({});
               setIsEditMode(false);
               return;
            }

            // Let react-select handle arrow navigation naturally
            if (isArrowDown || isArrowUp) {
               // Don't prevent default - let react-select handle the navigation
               return;
            }

            // Handle Enter and Tab for selection
            if ((isEnter || isTab) && menuIsOpen) {
               const focusedOption = selectRef.current?.state?.focusedOption;
               if (focusedOption) {
                  handleOptionSelect(focusedOption);
                  e.preventDefault();

                  // Focus next element for Tab
                  if (isTab && props.nextRef?.current) {
                     setTimeout(() => {
                        props.nextRef.current.focus();
                     }, 0);
                  }
               }
            }

            // Handle Enter without menu open
            if (isEnter && !menuIsOpen && props.nextRef?.current) {
               setTimeout(() => {
                  props.nextRef.current.focus();
               }, 0);
            }

            if (isShiftTab && props.preRef?.current) {
               setTimeout(() => {
                  props.preRef.current.focus();
               }, 0);
            }

            if ((e.ctrlKey && e.key.toLowerCase() === 'o') || e.key === 'F2') {
               e.preventDefault();
               const focusedOption = selectRef.current?.state?.focusedOption;
               if (focusedOption) {
                  setIsEditMode(true);
                  setCurrentRow(focusedOption);
                  setIsOpen(true);
               }
            }
         },
         [onKeyDown, menuIsOpen, inputValue, props.nextRef, props.preRef, handleOptionSelect],
      );

      // Change handler
      const handleChange = useCallback(
         (selected, actionMeta) => {
            if (isMulti) {
               if (!selected || selected.length === 0) {
                  onChange([]);
                  return;
               }

               const isSelectAllClicked = selected.find((opt) => opt.value === '*');
               const allOptionValues = mappedOptions.filter((opt) => opt.value !== '*').map((opt) => opt.value);

               if (isSelectAllClicked && actionMeta.action === 'select-option') {
                  onChange(selected.length === mappedOptions.length ? [] : allOptionValues);
                  return;
               }

               if (actionMeta.action === 'deselect-option' && selected.length + 1 === mappedOptions.length) {
                  onChange([]);
                  return;
               }

               const values = selected.map((item) => item.value).filter((val) => val !== '*');
               onChange(values);
            } else {
               onChange(selected ? selected.value : '');
            }
         },
         [isMulti, onChange, mappedOptions],
      );

      // Handle save from modal
      const handleSave = useCallback(
         (formData) => {
            setMenuIsOpen(false);
            setIsFocused(false);
            onClose();
            const newOption = { ...formData, label: formData.name, value: formData.id };
            onChange(newOption.value);

            props.handleStockModalOnClick && props.handleStockModalOnClick(props.rowIndex);
            if (props.nextRef?.current) {
               setTimeout(() => {
                  props.nextRef.current.focus();
               }, 0);
            }
         },
         [onClose, onChange, props],
      );

      const handleClose = useCallback(async () => {
         if (openfrom === 'salesdaybook') {
            await dispatch(getAllDaybooks({ type: type, companyId: Number(user?.loginhistory?.companyId) }));
         }
         setIsOpen(false);
         // setMenuIsOpen(false);
      }, []);

      // Imperative handle for ref
      useImperativeHandle(ref, () => ({
         focus: () => {
            if (selectRef.current) {
               selectRef.current.focus();
               if (isRequired) {
                  setMenuIsOpen(true);
               }
            }
         },
      }));

      // Custom components based on configuration
      const customComponents = useMemo(() => {
         const components = {};

         if (isMulti) {
            components.Option = CheckboxOption;
            components.ValueContainer = (props) => (
               <CustomValueContainer
                  {...props}
                  selectProps={{
                     ...props.selectProps,
                     setMenuIsOpen,
                     totalOptions: mappedOptions.length - 1, // Exclude "Select All"
                     isMulti: true, // Ensure isMulti is passed
                     controlShouldRenderValue: true, // Add this if needed
                  }}
               />
            );
         } else {
            components.Option = (props) => (
               <OptimizedOption
                  {...props}
                  isMulti={isMulti}
                  selectProps={{
                     ...props.selectProps,
                     showBalance: ['salesdaybook', 'daybook'].includes(openfrom),
                     showEdit: showCustomNoOptions,
                     openfrom,
                     hideStock,
                     daybook,
                     handleEdit: (option) => {
                        setIsEditMode(true);
                        setCurrentRow(option);
                        setIsOpen(true);
                     },
                  }}
               />
            );

            // Use virtualization for large datasets
            if (virtualized && filteredOptions.length > 100) {
               components.MenuList = (props) => (
                  <VirtualizedMenuList
                     {...props}
                     options={filteredOptions}
                     maxHeight={maxMenuHeight}
                     selectProps={{ ...props.selectProps, openfrom }}
                  />
               );
            }
         }

         if (showCustomNoOptions) {
            components.NoOptionsMessage = (props) => (
               <CustomNoOptionsMessage
                  {...props}
                  selectProps={{
                     ...props.selectProps,
                     showCustomNoOptions,
                     onAddNew: (inputValue) => {
                        setIsOpen(true);
                        setDemoname(inputValue);
                        setCurrentRow({});
                        setIsEditMode(false);
                     },
                  }}
               />
            );
         }

         return components;
      }, [isMulti, showCustomNoOptions, mappedOptions.length, virtualized, filteredOptions, openfrom, maxMenuHeight]);

      const valueColor = useColorModeValue('#000000', '#ffffff');
      const labelColor = useColorModeValue('#000000', '#ffffff');
      const bgcolor = useColorModeValue('#ffffff', '#101f4a');

      return (
         <>
            <FormControl position="relative" isRequired={isRequired} isInvalid={isInvalid}>
               <Box position="relative" zIndex={1}>
                  <Select
                     {...props}
                     id={name}
                     ref={selectRef}
                     menuIsOpen={menuIsOpen}
                     onMenuOpen={() => {
                        setMenuIsOpen(true);
                        setIsFocused(true);
                     }}
                     onMenuClose={() => {
                        setMenuIsOpen(false);
                        setIsFocused(false);
                     }}
                     onKeyDown={handleKeyDown}
                     value={selectedOption}
                     onChange={handleChange}
                     onFocus={() => {
                        if (isRequired) {
                           setMenuIsOpen(true);
                        }
                        setIsFocused(true);
                     }}
                     onBlur={() => {
                        setIsFocused(false);
                        setMenuIsOpen(false);
                     }}
                     options={filteredOptions}
                     isMulti={isMulti}
                     placeholder=""
                     isClearable={props.isClearable}
                     isSearchable
                     menuPlacement="auto"
                     onInputChange={setInputValue}
                     inputValue={inputValue}
                     closeMenuOnSelect={!isMulti}
                     hideSelectedOptions={false}
                     menuPortalTarget={document.body}
                     components={customComponents}
                     filterOption={() => true} // We handle filtering manually for better performance
                     isDisabled={props.disabled}
                     styles={{
                        control: (base, state) => ({
                           ...base,
                           borderRadius: '6px',
                           width: '100%',
                           borderColor: errors[errorName] ? 'red' : state.isFocused ? 'blue' : '#CBD5E0',
                           boxShadow: errors[errorName]
                              ? '0 0 0 1px #e53e3e'
                              : state.isFocused
                                 ? '0 0 0 1px #3182CE'
                                 : 'none',
                           backgroundColor: state.isDisabled ? '#EDF2F7' : bg,
                           color: state.isDisabled ? '#A0AEC0' : fontcolor,
                           cursor: state.isDisabled ? 'not-allowed' : 'default',
                           opacity: state.isDisabled ? 0.8 : 1,
                           '&:hover': { borderColor: state.isDisabled ? '#CBD5E0' : '#A0AEC0' },
                           minHeight: '40px',
                        }),
                        menu: (base) => ({
                           ...base,
                           zIndex: 9999,
                           position: 'absolute',
                           maxHeight: maxMenuHeight,
                           backgroundColor: bgcolor,
                        }),
                        option: (base, { isFocused, isSelected, isDisabled }) => ({
                           ...base,
                           backgroundColor: isDisabled ? '#F7FAFC' : isSelected && !isMulti ? '#3182ce' : isFocused ? '#ebf8ff' : 'white',
                           color: isDisabled ? '#A0AEC0' : isSelected && !isMulti ? 'white' : 'black',
                           cursor: isDisabled ? 'not-allowed' : 'pointer',
                           padding: '8px 12px',
                        }),
                        menuPortal: (base) => ({
                           ...base,
                           zIndex: 9999,
                        }),
                        valueContainer: (base) => ({
                           ...base,
                           padding: '2px 8px',
                        }),
                        input: (base, state) => ({
                           ...base,
                           margin: 0,
                           paddingBottom: 0,
                           paddingTop: 0,
                           color: state.isDisabled ? '#A0AEC0' : valueColor,
                        }),
                        singleValue: (base, state) => ({
                           ...base,
                           color: state.isDisabled ? '#A0AEC0' : valueColor,
                        }),
                     }}
                  />

                  <FormLabel
                     htmlFor={name}
                     position="absolute"
                     top={isFloating ? '-8px' : '8px'}
                     left="12px"
                     fontSize={isFloating ? '12px' : '16px'}
                     isDisabled={props.disabled}
                     color={labelColor}
                     bg={props.disabled ? `linear-gradient(to bottom, ${bg} 50%, ${bg2} 50%)` : bg}
                     // color={fontcolor}
                     px="4px"
                     transition="all 0.2s ease-in-out"
                     pointerEvents="none">
                     {label}
                  </FormLabel>

                  {errors[errorName ?? name] && <FormErrorMessage>{errors[errorName ?? name]}</FormErrorMessage>}
               </Box>
            </FormControl>

            {/* Modal forms would go here - same as your original implementation */}

            {openfrom === 'item' && (
               <>
                  <ItemMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'city' && (
               <>
                  <CityMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'state' && (
               <>
                  <StateMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'country' && (
               <>
                  <CountryMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'transport' && (
               <>
                  <TransportMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'bankBranch' && (
               <>
                  <BankAccountsForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'ledger' && (
               <>
                  <LedgerMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'salesRepresentative' && (
               <>
                  <SalesRepresentativeMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'brand' && (
               <>
                  <BrandMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'hsn' && (
               <>
                  <HsnMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'account' && (
               <>
                  <AddEditForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     initialData={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                     formType="Account"
                  />
               </>
            )}
            {openfrom === 'itemgroups' && (
               <>
                  <ItemGroupMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'wareHouse' && (
               <>
                  <WarehouseMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'daybook' && (
               <>
                  <DaybookForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'salesdaybook' && (
               <>
                  <DaybookForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                     apitype={type}
                  />
               </>
            )}
            {openfrom === 'series' && (
               <>
                  <SeriesMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'color' && (
               <>
                  <ColorMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'vehicle' && (
               <>
                  <VehicleMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     demoname={demoname}
                     currentRow={currentRow}
                     onSave={handleSave}
                     saveandcontinue={false}
                  />
               </>
            )}
            {openfrom === 'Saleman' && (
               <>
                  <AddUserModal
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     userId={currentRow.id}
                     // isRoleModalOpen={isRoleModalOpen}
                     // setIsRoleModalOpen={setIsRoleModalOpen}
                     // selectedMember={selectedMember}
                     // companyId={companyId}
                     username={demoname}
                     usertype={1}
                     companyList={companyList}
                     onSave={handleSave}
                     isOpenRoleModal={false}
                  />
               </>
            )}
            {openfrom === 'Screen' && (
               <>
                  <ScreenMasterForm
                     isOpen={isOpen}
                     onClose={handleClose}
                     isEditMode={isEditMode}
                     editData={currentRow}
                     onSave={handleSave}
                  />
               </>
            )}
         </>
      );
   },
);

DynamicDropdown.displayName = 'DynamicDropdown';

export default DynamicDropdown;

export const renderDynamicDropdown = (
   name,
   label,
   options,
   props,
   errors = {},
   textColor = 'gray.700',
   isRequired = false,
   placeholder = 'Select an option',
   isSearchable = true,
   filteredOptions,
   isMulti = false,
   onKeyDown,
) => {
   return (
      <DynamicDropdown
         name={name}
         label={label}
         options={options}
         openfrom={props.openfrom}
         hideStock={props.hideStock}
         daybook={props.daybook}
         value={props.value}
         showCustomNoOptions={props.showCustomNoOptions}
         errors={errors}
         errorName={props.errorName}
         textColor={textColor}
         onChange={props.onChange}
         selectRef={props.selectRef}
         isRequired={isRequired}
         placeholder={placeholder}
         isSearchable={isSearchable}
         filteredOptions={filteredOptions}
         onKeyDown={onKeyDown}
         {...props}
         isMulti={isMulti}
      />
   );
};
